import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import "./dashboard.style.css";
import { isOwnerOrFinance } from "../../utils/role-check";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import DonutChart from "../Charts/DonutChart";
import DashboardCalendar from "./DashboardCalendar";
import AgendaWidget from "./AgendaWidget";

function DashboardComponent({
  auth,
  projects,
  timesheets,
  users,
  userDocuments,
  totalLaborCosts,
  totalRegisteredHours,
  totalRegisteredMeters,
  totalExtraMaterialCosts,
  usersWithInvalidDocs,
  loading,
  error
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Collapsible sections for mobile - default based on role
  const defaultSection = isOwnerOrFinance(auth.role) ? 'financial' : 'agenda';
  const [openSection, setOpenSection] = useState(defaultSection);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Memoize expensive calculations - MUST be before any conditional returns
  const calculations = useMemo(() => {
    const totalMaterialCosts = projects && projects.length > 0
      ? projects.reduce((acc, p) => acc + (typeof p.materialcosts === 'number' ? p.materialcosts : 0), 0)
      : 0;

    const totalProjectPrice = projects && projects.length > 0
      ? projects.reduce((acc, p) => acc + (typeof p.projectprice === 'number' ? p.projectprice : 0), 0)
      : 0;

    return {
      totalMaterialCosts,
      totalHours: totalRegisteredHours || 0,
      totalMeters: totalRegisteredMeters || 0,
      totalProjectPrice,
      totalEmployeeCosts: totalLaborCosts || 0,
      totalExtraMaterialCosts: totalExtraMaterialCosts || 0
    };
  }, [projects, totalLaborCosts, totalRegisteredHours, totalRegisteredMeters, totalExtraMaterialCosts]);

  const assignedProjects = useMemo(() => {
    return auth && Array.isArray(projects) ? projects.slice(0, 3) : [];
  }, [auth, projects]);

  const latest3Timesheets = useMemo(() => {
    return Array.isArray(timesheets)
      ? timesheets
        .filter(ts => ts && ts.id)
        .sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0))
        .slice(0, 3)
      : [];
  }, [timesheets]);

  // Helper for document status and missing documents
  const requiredTypes = useMemo(() => [
    { type: "vca diploma", normalized: "vcadiploma" },
    { type: "kvk-uittreksel", normalized: "kvkuittreksel" },
    { type: "verzekeringspolis", normalized: "verzekeringspolis" }
  ], []);

  // Helper function to normalize document category for comparison
  const normalizeDocCategory = (category) => {
    if (!category) return '';
    return category.toLowerCase().replace(/[\s\-_]/g, '');
  };

  // Use usersWithInvalidDocs from backend if available, otherwise calculate it
  const invalidUsers = useMemo(() => {
    console.log("[DASHBOARD] 🔍 Processing invalid users...");
    console.log("[DASHBOARD] Backend usersWithInvalidDocs:", usersWithInvalidDocs);
    console.log("[DASHBOARD] Is array?", Array.isArray(usersWithInvalidDocs), "Length:", usersWithInvalidDocs?.length);

    // If backend provides the invalid users list, use it
    if (usersWithInvalidDocs && Array.isArray(usersWithInvalidDocs) && usersWithInvalidDocs.length > 0) {
      console.log("[DASHBOARD] ✅ Using BACKEND data - Processing", usersWithInvalidDocs.length, "invalid doc entries");
      // Group documents by user_id
      const userMap = new Map();

      usersWithInvalidDocs.forEach(doc => {
        if (!userMap.has(doc.user_id)) {
          userMap.set(doc.user_id, {
            id: doc.user_id,
            name: doc.user_name || t('common.unknown'),
            email: doc.user_email || t('common.noEmail'),
            missingDocs: [],
            expiredDocs: []
          });
        }

        const userData = userMap.get(doc.user_id);
        if (doc.is_missing) {
          userData.missingDocs.push(doc.document_type);
        } else if (doc.is_expired) {
          userData.expiredDocs.push(doc.document_type);
        }
      });

      return Array.from(userMap.values()).sort((a, b) => {
        // Users with expired docs come first
        if (b.expiredDocs.length !== a.expiredDocs.length) {
          return b.expiredDocs.length - a.expiredDocs.length;
        }
        // Then by missing docs
        return b.missingDocs.length - a.missingDocs.length;
      });
    }

    // Fallback to frontend calculation if backend doesn't provide it
    console.log("[DASHBOARD] ⚠️ Using FRONTEND calculation (backend returned empty/null)");
    console.log("[DASHBOARD] Frontend users prop:", users);
    console.log("[DASHBOARD] Frontend userDocuments prop:", userDocuments);

    const getDocumentStatusAndMissing = (documents, user) => {
      const now = new Date();
      let hasMissing = false;
      let hasExpired = false;
      let missingDocs = [];
      let expiredDocs = [];
      for (const req of requiredTypes) {
        const docs = (documents || []).filter(doc =>
          normalizeDocCategory(doc.document_category) === req.normalized
        );
        if (docs.length === 0) {
          hasMissing = true;
          missingDocs.push(req.type);
          continue;
        }
        const hasValid = docs.some(doc => {
          const exp = doc.expires_at ? new Date(doc.expires_at) : null;
          const uploaded = doc.created_at ? new Date(doc.created_at) : null;
          if (!exp) return true;
          if (uploaded && exp && uploaded > exp) return false;
          return exp >= now;
        });
        if (!hasValid) {
          hasExpired = true;
          docs.forEach(doc => {
            const exp = doc.expires_at ? new Date(doc.expires_at) : null;
            const uploaded = doc.created_at ? new Date(doc.created_at) : null;
            if (exp && uploaded && exp < now) {
              expiredDocs.push(req.type);
            }
          });
        }
      }
      // Check for missing profile picture
      if (!user || !user.profile_picture) {
        hasMissing = true;
        missingDocs.push("profiel foto");
      }
      let status = "valid";
      if (hasMissing) status = "missing";
      else if (hasExpired) status = "expired";
      return { status, missingDocs, expiredDocs };
    };

    // Sort invalid users: expired first, then missing
    return users
      .map(user => {
        const { status, missingDocs, expiredDocs} = getDocumentStatusAndMissing(userDocuments[user.id], user);
        if (status === "expired" || status === "missing") {
          return {
            ...user,
            email: user.email || t('common.noEmail'),
            missingDocs,
            expiredDocs
          };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        // Users with expired docs come first
        if (b.expiredDocs.length !== a.expiredDocs.length) {
          return b.expiredDocs.length - a.expiredDocs.length;
        }
        // Then by missing docs
        return b.missingDocs.length - a.missingDocs.length;
      });
  }, [users, userDocuments, requiredTypes, usersWithInvalidDocs]);

  // Calculate quarterly data for dashboard
  const quarterlyData = useMemo(() => {
    const VAT_RATE = 0.21;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    const quarters = [];

    for (let q = 1; q <= 4; q++) {
      const quarterStartMonth = (q - 1) * 3;
      const periodStart = new Date(currentYear, quarterStartMonth, 1);
      const periodEnd = new Date(currentYear, quarterStartMonth + 3, 0);

      // Filter projects for this quarter
      const quarterProjects = projects.filter(project => {
        if (!project.end_date) return false;
        const projectStart = new Date(project.start_date);
        const projectEnd = new Date(project.end_date);
        return projectStart <= periodEnd && projectEnd >= periodStart;
      });

      // Calculate totals
      const totalRevenue = quarterProjects.reduce((sum, project) => sum + (project.projectprice || 0), 0);
      const totalCosts = quarterProjects.reduce((sum, project) => sum + (project.materialcosts || 0), 0);
      const totalProfit = quarterProjects.reduce((sum, project) => sum + (project.profit || 0), 0);
      const revenueExclVAT = totalRevenue / (1 + VAT_RATE);
      const vatAmount = totalRevenue - revenueExclVAT;

      quarters.push({
        quarter: q,
        periodStart,
        periodEnd,
        projectCount: quarterProjects.length,
        totalRevenue,
        revenueExclVAT,
        vatAmount,
        totalCosts,
        totalProfit,
        isCurrentQuarter: q === currentQuarter
      });
    }

    // Calculate year totals
    const yearTotal = {
      projectCount: quarters.reduce((sum, q) => sum + q.projectCount, 0),
      totalRevenue: quarters.reduce((sum, q) => sum + q.totalRevenue, 0),
      revenueExclVAT: quarters.reduce((sum, q) => sum + q.revenueExclVAT, 0),
      vatAmount: quarters.reduce((sum, q) => sum + q.vatAmount, 0),
      totalCosts: quarters.reduce((sum, q) => sum + q.totalCosts, 0),
      totalProfit: quarters.reduce((sum, q) => sum + q.totalProfit, 0),
    };

    return { quarters, yearTotal, currentYear };
  }, [projects]);

  // NOW we can have conditional returns
  if (loading) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={error} />;

  // Helper for status badge mapping
  const getStatusBadge = (status) => {
    switch (status) {
      case "approved_by_voorman":
        return <span className="status-badge-dashboard status-approved_by_voorman">{t('timesheets.approvedByVoorman')}</span>;
      case "approved":
        return <span className="status-badge-dashboard status-approved">{t('timesheets.approved')}</span>;
      case "rejected":
        return <span className="status-badge-dashboard status-rejected">{t('timesheets.rejected')}</span>;
      case "pending":
        return <span className="status-badge-dashboard status-pending">{t('timesheets.pending')}</span>;
      default:
        return <span className="status-badge-dashboard">{status.replace(/_/g, ' ')}</span>;
    }
  };

  return (
    <div className="dashboard" role="main">
      {/* Consolidated Financial Dashboard - Only for eigenaar/financieel medewerker */}
      {isOwnerOrFinance(auth.role) && (
        <section className={`dashboard-card full-width consolidated-financial-dashboard collapsible-card ${openSection === 'financial' ? 'open' : ''}`} aria-label={t('dashboard.financialDashboard')}>
          <div className="financial-dashboard-header collapsible-header" onClick={() => toggleSection('financial')}>
            <h3>
              <span>{t('dashboard.financial')} {quarterlyData.currentYear}</span>
              <span className="collapse-icon">{openSection === 'financial' ? '▼' : '▶'}</span>
            </h3>
          </div>
          <div className="collapsible-content">{/* Wraps all financial content */}

          {/* Top Level Summary Cards */}
          <div className="financial-summary-grid">
            <div className="summary-card highlight">
              <div className="summary-card-icon revenue-icon"></div>
              <div className="summary-card-content">
                <div className="summary-card-label">{t('dashboard.totalRevenueInclVAT')}</div>
                <div className="summary-card-value primary">
                  € {quarterlyData.yearTotal.totalRevenue.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="summary-card-meta">
                  {quarterlyData.yearTotal.projectCount} {t('dashboard.projects')}
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-card-icon costs-icon"></div>
              <div className="summary-card-content">
                <div className="summary-card-label">{t('dashboard.totalCosts')}</div>
                <div className="summary-card-value">
                  € {(calculations.totalEmployeeCosts + calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts).toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="summary-card-breakdown">
                  <small>{t('dashboard.labor')}: € {calculations.totalEmployeeCosts.toLocaleString(t('common.locale'), { maximumFractionDigits: 0 })} | {t('dashboard.materials')}: € {(calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts).toLocaleString(t('common.locale'), { maximumFractionDigits: 0 })}</small>
                </div>
              </div>
            </div>

            <div className="summary-card highlight">
              <div className="summary-card-icon profit-icon"></div>
              <div className="summary-card-content">
                <div className="summary-card-label">{t('dashboard.fiscalProfit')}</div>
                <div className={`summary-card-value ${(quarterlyData.yearTotal.totalRevenue - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts) >= 0 ? "positive" : "negative"}`}>
                  € {Math.abs(quarterlyData.yearTotal.totalRevenue - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts).toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="summary-card-meta">
                  {t('dashboard.margin')}: {calculations.totalProjectPrice > 0 ? ((calculations.totalProjectPrice - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts) / calculations.totalProjectPrice * 100).toFixed(1) : "0.0"}%
                </div>
              </div>
            </div>

            <div className="summary-card vat-card">
              <div className="summary-card-icon vat-icon"></div>
              <div className="summary-card-content">
                <div className="summary-card-label">{t('dashboard.vatReservation')} (21%)</div>
                <div className="summary-card-value vat-amount">
                  € {quarterlyData.yearTotal.vatAmount.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="summary-card-meta">
                  {t('dashboard.exclVAT')}: € {quarterlyData.yearTotal.revenueExclVAT.toLocaleString(t('common.locale'), { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </div>

          {/* Tax Calculation Insight */}
          <div className="tax-insight-section">
            <div className="tax-insight-header">
              <h3>{t('dashboard.taxReservation')}</h3>
              <div className="tax-warning-badge">{t('dashboard.important')}</div>
            </div>
            <div className="tax-calculation-grid">
              <div className="tax-calc-row">
                <div className="tax-calc-label">{t('dashboard.totalRevenue')}</div>
                <div className="tax-calc-value positive">
                  € {quarterlyData.yearTotal.totalRevenue.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="tax-calc-row">
                <div className="tax-calc-label">{t('dashboard.totalCosts')}</div>
                <div className="tax-calc-value negative">
                  - € {(calculations.totalEmployeeCosts + calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts).toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="tax-calc-divider"></div>
              <div className="tax-calc-row highlight">
                <div className="tax-calc-label"><strong>{t('dashboard.fiscalProfit')}</strong></div>
                <div className="tax-calc-value">
                  <strong>€ {(quarterlyData.yearTotal.totalRevenue - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts).toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}</strong>
                </div>
              </div>
              <div className="tax-calc-row tax-row">
                <div className="tax-calc-label">
                  {t('dashboard.estimatedIncomeTax')} (36.97% / 49.5%)
                  <small>{t('dashboard.taxBrackets')}</small>
                </div>
                <div className="tax-calc-value tax">
                  € {(() => {
                    const fiscaleWinst = quarterlyData.yearTotal.totalRevenue - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts;
                    if (fiscaleWinst <= 0) return "0.00";
                    const bracket1Limit = 75518;
                    const rate1 = 0.3697;
                    const rate2 = 0.495;
                    let tax = 0;
                    if (fiscaleWinst <= bracket1Limit) {
                      tax = fiscaleWinst * rate1;
                    } else {
                      tax = (bracket1Limit * rate1) + ((fiscaleWinst - bracket1Limit) * rate2);
                    }
                    return tax.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 });
                  })()}
                </div>
              </div>
              <div className="tax-calc-divider"></div>
              <div className="tax-calc-row final">
                <div className="tax-calc-label"><strong>{t('dashboard.netProfitAfterTax')}</strong></div>
                <div className="tax-calc-value positive">
                  <strong>€ {(() => {
                    const fiscaleWinst = quarterlyData.yearTotal.totalRevenue - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts;
                    if (fiscaleWinst <= 0) return fiscaleWinst.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 });
                    const bracket1Limit = 75518;
                    const rate1 = 0.3697;
                    const rate2 = 0.495;
                    let tax = 0;
                    if (fiscaleWinst <= bracket1Limit) {
                      tax = fiscaleWinst * rate1;
                    } else {
                      tax = (bracket1Limit * rate1) + ((fiscaleWinst - bracket1Limit) * rate2);
                    }
                    return (fiscaleWinst - tax).toLocaleString(t('common.locale'), { minimumFractionDigits: 2 });
                  })()}</strong>
                </div>
              </div>
            </div>
            <div className="tax-info-note">
              <small>
                <strong>{t('dashboard.note')}:</strong> {t('dashboard.vatQuarterlyNote', { vat: quarterlyData.yearTotal.vatAmount.toLocaleString(t('common.locale'), { maximumFractionDigits: 0 }) })}
                {' '}{t('dashboard.incomeTaxDisclaimer')}
              </small>
            </div>
          </div>

          {/* Quarterly Breakdown */}
          <div className="quarterly-breakdown-section">
            <h3>{t('dashboard.quarterlyBreakdown')} {quarterlyData.currentYear}</h3>
            <div className="quarters-grid">
              {quarterlyData.quarters.map((quarter) => (
                <div
                  key={quarter.quarter}
                  className={`quarter-card ${quarter.isCurrentQuarter ? 'current-quarter' : ''} ${quarter.projectCount === 0 ? 'empty-quarter' : ''}`}
                >
                  <div className="quarter-card-header">
                    <h3>Q{quarter.quarter}</h3>
                    <span className="quarter-period">
                      {quarter.periodStart.toLocaleDateString(t('common.locale'), { day: 'numeric', month: 'short' })} - {quarter.periodEnd.toLocaleDateString(t('common.locale'), { day: 'numeric', month: 'short' })}
                    </span>
                    {quarter.isCurrentQuarter && <span className="current-badge">{t('dashboard.current')}</span>}
                  </div>

                  <div className="quarter-stats">
                    <div className="quarter-stat-row">
                      <span className="stat-label">{t('dashboard.projects')}</span>
                      <span className="stat-value">{quarter.projectCount}</span>
                    </div>
                    <div className="quarter-stat-row highlight">
                      <span className="stat-label">{t('dashboard.revenueInclVAT')}</span>
                      <span className="stat-value">€ {quarter.totalRevenue.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="quarter-stat-row">
                      <span className="stat-label">{t('dashboard.revenueExclVAT')}</span>
                      <span className="stat-value">€ {quarter.revenueExclVAT.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="quarter-stat-row vat-row">
                      <span className="stat-label">{t('dashboard.vat')} (21%)</span>
                      <span className="stat-value vat-amount">€ {quarter.vatAmount.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="quarter-stat-row">
                      <span className="stat-label">{t('dashboard.costs')}</span>
                      <span className="stat-value">€ {quarter.totalCosts.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="quarter-stat-row profit-row">
                      <span className="stat-label">{t('dashboard.profit')}</span>
                      <span className="stat-value">€ {quarter.totalProfit.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Breakdown with Chart */}
          <div className="detailed-breakdown-section">
            <h3>{t('dashboard.detailedAnalysis')}</h3>
            <div className="breakdown-grid">
              <div className="breakdown-column">
                <h4>{t('dashboard.costStructure')}</h4>
                <div className="breakdown-list">
                  <div className="breakdown-item">
                    <div className="breakdown-label">
                      <span className="breakdown-dot" style={{ backgroundColor: '#7393b0' }}></span>
                      {t('dashboard.laborCosts')}
                    </div>
                    <div className="breakdown-value">
                      € {calculations.totalEmployeeCosts.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                    </div>
                    <div className="breakdown-percentage">
                      {((calculations.totalEmployeeCosts / (calculations.totalEmployeeCosts + calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts) * 100) || 0).toFixed(1)}%
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-label">
                      <span className="breakdown-dot" style={{ backgroundColor: '#565a5e' }}></span>
                      {t('dashboard.materialCosts')}
                    </div>
                    <div className="breakdown-value">
                      € {calculations.totalMaterialCosts.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                    </div>
                    <div className="breakdown-percentage">
                      {((calculations.totalMaterialCosts / (calculations.totalEmployeeCosts + calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts) * 100) || 0).toFixed(1)}%
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-label">
                      <span className="breakdown-dot" style={{ backgroundColor: '#cdcdce' }}></span>
                      {t('dashboard.extraMaterials')}
                    </div>
                    <div className="breakdown-value">
                      € {calculations.totalExtraMaterialCosts.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                    </div>
                    <div className="breakdown-percentage">
                      {((calculations.totalExtraMaterialCosts / (calculations.totalEmployeeCosts + calculations.totalMaterialCosts + calculations.totalExtraMaterialCosts) * 100) || 0).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="breakdown-column">
                <h4>{t('dashboard.laborInformation')}</h4>
                <div className="breakdown-list">
                  <div className="breakdown-item">
                    <div className="breakdown-label">{t('dashboard.registeredHours')}</div>
                    <div className="breakdown-value">{Number(calculations.totalHours).toLocaleString(t('common.locale'), { minimumFractionDigits: 0 })} {t('common.hours')}</div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-label">{t('dashboard.registeredMeters')}</div>
                    <div className="breakdown-value">{Number(calculations.totalMeters).toLocaleString(t('common.locale'), { minimumFractionDigits: 1 })} m</div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-label">{t('dashboard.avgHourlyRate')}</div>
                    <div className="breakdown-value">€ {calculations.totalHours > 0 ? (calculations.totalEmployeeCosts / calculations.totalHours).toLocaleString(t('common.locale'), { minimumFractionDigits: 2 }) : "0.00"}</div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-label">{t('dashboard.revenuePerHour')}</div>
                    <div className="breakdown-value">€ {calculations.totalHours > 0 ? (calculations.totalProjectPrice / calculations.totalHours).toLocaleString(t('common.locale'), { minimumFractionDigits: 2 }) : "0.00"}</div>
                  </div>
                </div>
              </div>

              <div className="breakdown-column chart-column">
                <h4>{t('dashboard.costDistribution')}</h4>
                <div className="totals-piechart-wrapper">
                  <DonutChart
                    data={[
                      { name: t('dashboard.laborCosts'), value: Math.abs(calculations.totalEmployeeCosts), color: '#7393b0' },
                      { name: t('dashboard.materialCosts'), value: Math.abs(calculations.totalMaterialCosts), color: '#565a5e' },
                      { name: t('dashboard.extraMaterials'), value: Math.abs(calculations.totalExtraMaterialCosts), color: '#cdcdce' },
                      ...((calculations.totalProjectPrice - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts) > 0 ? [{
                        name: t('dashboard.profit'),
                        value: calculations.totalProjectPrice - calculations.totalEmployeeCosts - calculations.totalMaterialCosts - calculations.totalExtraMaterialCosts,
                        color: '#4ade80'
                      }] : [])
                    ].filter(item => item.value > 0)}
                    showLegend={true}
                  />
                </div>
              </div>
            </div>
          </div>
          </div>{/* End collapsible-content for financial dashboard */}
        </section>
      )}

      {/* Two cards side by side on desktop, stacked on mobile */}
      <div className="dashboard-row" style={{gap: '1.5rem'}}>
        {/* Assigned Projects Card */}
        <section className={`dashboard-card assigned-projects-card collapsible-card ${openSection === 'projects' ? 'open' : ''}`} aria-label={t('dashboard.myProjects')}>
          <h3 onClick={() => toggleSection('projects')} className="collapsible-header">
            <span>{t('dashboard.myProjects')}</span>
            <span className="collapse-icon">{openSection === 'projects' ? '▼' : '▶'}</span>
          </h3>
          <div className="collapsible-content">
            {(!assignedProjects || assignedProjects.length === 0) ? (
              <NoDataConstruction message={t('dashboard.noProjects')} />
            ) : (
              <ul>
                {assignedProjects.map(project => (
                  <li key={project.id} style={{ cursor: "pointer" }} tabIndex={0} aria-label={`Project ${project.name}`}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      onKeyPress={e => { if (e.key === 'Enter') navigate(`/projects/${project.id}`); }}>
                    <strong>{project.name}</strong>
                    <span className="project-meta">{project.client} ({project.location})</span>
                    {isOwnerOrFinance(auth.role) && typeof project.profit === 'number' && (
                      <span className={`profit ${project.profit >= 0 ? "positive" : "negative"}`}>
                        {project.profit >= 0 ? t('dashboard.profit') : t('dashboard.loss')}: € {project.profit.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <Link to="/projects" className="btn-view">{t('dashboard.viewAllProjects')}</Link>
          </div>
        </section>

        {/* Last 3 Timesheets Card */}
        { !auth.role === "klant" && (
          <section className={`dashboard-card last-timesheets-card collapsible-card ${openSection === 'timesheets' ? 'open' : ''}`} aria-label={t('dashboard.lastTimesheets')}>
            <h3 onClick={() => toggleSection('timesheets')} className="collapsible-header">
              <span>{t('dashboard.lastTimesheets')}</span>
              <span className="collapse-icon">{openSection === 'timesheets' ? '▼' : '▶'}</span>
            </h3>
            <div className="collapsible-content">
            {(!latest3Timesheets || latest3Timesheets.length === 0) ? (
              <div style={{ textAlign: 'center', width: '100%', padding: '2rem 0' }}>
                <NoDataConstruction text={t('dashboard.noTimesheets')}/>
              </div>
            ) : (
              <div className="dashboard-timesheet-cards">
                {latest3Timesheets.map(ts => (
                  <div
                    key={ts.id}
                    className="timesheet-card-dashboard"
                    tabIndex={0}
                    aria-label={`${t('timesheet.title')} ${t('common.for')} ${t('timesheet.project')} ${ts.project?.name || t('common.unknown')}`}
                    onClick={() => navigate(`/timesheets/${ts.id}`)}
                    onKeyPress={e => { if (e.key === 'Enter') navigate(`/timesheets/${ts.id}`); }}
                  >
                    <div className="timesheet-card-header">
                      <span className="timesheet-project">{ts.project?.name || t('common.unknown')}</span>
                      <span className="timesheet-week">{t('timesheet.week')} {ts.week_number}</span>
                      <span className="timesheet-week">{ts.user?.name}</span>
                    </div>
                    <div className="timesheet-card-body">
                    <span className="timesheet-status">{getStatusBadge(ts.status)}</span>
                      <span className="timesheet-hours">{t('timesheet.totalHoursShort')} {typeof ts.total_hours === "number" ? ts.total_hours : "-"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </section>
      )}
      </div>

      {/* Calendar Widget - Available for all users */}
      {isOwnerOrFinance(auth.role) && (
        <section className={`dashboard-card full-width collapsible-card ${openSection === 'calendar' ? 'open' : ''}`} aria-label={t('dashboard.planningOverview')}>
          <h3 onClick={() => toggleSection('calendar')} className="collapsible-header">
            <span>{t('dashboard.planningOverview')}</span>
            <span className="collapse-icon">{openSection === 'calendar' ? '▼' : '▶'}</span>
          </h3>
          <div className="collapsible-content">
            <DashboardCalendar projects={projects || []} assignments={[]} />
          </div>
        </section>
      )}

      {/* Users with invalid documents - Enhanced */}
      {isOwnerOrFinance(auth.role) && (
        <section className={`dashboard-card invalid-users-card full-width collapsible-card ${openSection === 'documents' ? 'open' : ''}`}>
          <h3 onClick={() => toggleSection('documents')} className="collapsible-header">
            <span>{t('dashboard.documentManagement')}</span>
            <span className="collapse-icon">{openSection === 'documents' ? '▼' : '▶'}</span>
          </h3>
          <div className="collapsible-content">

          {invalidUsers.length === 0 ? (
            <div className="no-issues-state">
              <div className="success-icon">✓</div>
              <p className="success-message">{t('dashboard.allDocumentsValid')}</p>
              <p className="success-submessage">{t('dashboard.noActionRequired')}</p>
            </div>
          ) : (
            <>
              {/* Summary Statistics */}
              <div className="doc-summary-grid">
                <div className="doc-summary-card total">
                  <div className="doc-summary-icon users-icon"></div>
                  <div className="doc-summary-content">
                    <div className="doc-summary-value">{invalidUsers.length}</div>
                    <div className="doc-summary-label">{t('dashboard.usersWithIssues')}</div>
                  </div>
                </div>
                <div className="doc-summary-card expired">
                  <div className="doc-summary-icon expired-icon"></div>
                  <div className="doc-summary-content">
                    <div className="doc-summary-value">{invalidUsers.filter(u => u.expiredDocs.length > 0).length}</div>
                    <div className="doc-summary-label">{t('dashboard.expiredDocuments')}</div>
                  </div>
                </div>
                <div className="doc-summary-card missing">
                  <div className="doc-summary-icon missing-icon"></div>
                  <div className="doc-summary-content">
                    <div className="doc-summary-value">{invalidUsers.filter(u => u.missingDocs.length > 0).length}</div>
                    <div className="doc-summary-label">{t('dashboard.missingDocuments')}</div>
                  </div>
                </div>
              </div>

              {/* User Cards Grid */}
              <div className="invalid-users-grid">
                {invalidUsers.map(user => {
                  const totalIssues = user.missingDocs.length + user.expiredDocs.length;
                  const severity = user.expiredDocs.length > 0 ? 'critical' : 'warning';
                  return (
                    <div key={user.id} className={`invalid-user-card severity-${severity}`}>
                      {/* Severity Badge */}
                      <div className={`severity-badge ${severity}`}>
                        {severity === 'critical' ? t('dashboard.urgent') : t('dashboard.attention')}
                      </div>

                      {/* User Header */}
                      <div className="invalid-user-header">
                        <div className="invalid-user-avatar">
                          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div className="invalid-user-info">
                          <div className="invalid-user-name">{user.name || t('common.unknown')}</div>
                          <div className="invalid-user-email">{user.email || t('common.noEmail')}</div>
                        </div>
                        <div className="issue-count-badge">{totalIssues}</div>
                      </div>

                      {/* Document Issues */}
                      <div className="invalid-user-docs">
                        {user.expiredDocs.length > 0 && (
                          <div className="doc-status-section has-expired">
                            <div className="doc-status-header">
                              <div className="doc-status-icon error"></div>
                              <span className="doc-status-label expired">
                                {t('dashboard.expired')} ({user.expiredDocs.length})
                              </span>
                            </div>
                            <div className="doc-status-tags">
                              {user.expiredDocs.map((doc) => (
                                <span key={doc} className="doc-tag expired">
                                  {doc.charAt(0).toUpperCase() + doc.slice(1).replace(/-/g, ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {user.missingDocs.length > 0 && (
                          <div className="doc-status-section has-missing">
                            <div className="doc-status-header">
                              <div className="doc-status-icon warning"></div>
                              <span className="doc-status-label missing">
                                {t('dashboard.missing')} ({user.missingDocs.length})
                              </span>
                            </div>
                            <div className="doc-status-tags">
                              {user.missingDocs.map((doc) => (
                                <span key={doc} className="doc-tag missing">
                                  {doc.charAt(0).toUpperCase() + doc.slice(1).replace(/-/g, ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="invalid-user-actions">
                        <button
                          type="button"
                          className="btn-action-primary"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          {t('nav.profile')}
                        </button>
                        <button
                          type="button"
                          className="btn-action-secondary"
                          onClick={() => navigate(`/users/${user.id}#documents`)}
                        >
                          {t('documents.title')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Action Link */}
              <div className="quick-action-footer">
                <Link to="/users" className="btn-view-all">
                  {t('dashboard.viewAllUsers')}
                </Link>
              </div>
            </>
          )}
          </div>{/* End collapsible-content for documents */}
        </section>
      )}

      {/* Agenda Widget for ZZP/Voorman users (non-finance) */}
      {!isOwnerOrFinance(auth.role) &&  !auth.role === "klant" && (
        <section className={`dashboard-card full-width collapsible-card ${openSection === 'agenda' ? 'open' : ''}`} aria-label={t('dashboard.myAgenda')}>
          <h3 onClick={() => toggleSection('agenda')} className="collapsible-header">
            <span>{t('dashboard.myAgenda')}</span>
            <span className="collapse-icon">{openSection === 'agenda' ? '▼' : '▶'}</span>
          </h3>
          <div className="collapsible-content">
            <AgendaWidget auth={auth} />
          </div>
        </section>
      )}
    </div>
  )
}

DashboardComponent.propTypes = {
    auth: PropTypes.object.isRequired,
    projects: PropTypes.array.isRequired,
    timesheets: PropTypes.array.isRequired,
    users: PropTypes.array.isRequired,
    userDocuments: PropTypes.object.isRequired,
    totalLaborCosts: PropTypes.number,
    totalRegisteredHours: PropTypes.number,
    totalRegisteredMeters: PropTypes.number,
    totalExtraMaterialCosts: PropTypes.number,
    usersWithInvalidDocs: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
};

export default DashboardComponent;
